import { Volume2, VolumeX, Loader2, RotateCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useWebRTC, type PlayerState } from "../../hooks/useWebRTC";
import clsx from "clsx";

interface Props {
  streamName: string | null;
  label?: string;
  showAudioToggle?: boolean;
  className?: string;
  // Callback opcional para que el padre se entere de transiciones de estado
  // del player (idle → connecting → playing → error/stopped). El padre lo usa
  // para coordinar transiciones suaves entre streams (double-buffer al cambiar
  // calidad o al maximizar).
  onStateChange?: (state: PlayerState) => void;
  // Disparado cuando el bitstream HEVC del NVR no es decodable por Chrome MSE
  // (mensaje "HTMLMediaElement.error attribute is not null" al appendBuffer).
  // El padre debe pedir el stream con transcode=true y pasar el nuevo
  // streamName. Mientras tanto el player muestra "Activando re-codificación...".
  onDecodeError?: () => void;
  // Disparado cuando un stream sufijo `_h264` falla por exec/ffmpeg (ej. RTSP
  // no responde via VPN y el wrapper ffmpeg corta con "Invalid data found").
  // El padre debe limpiar el flag transcode (localStorage) y reintentar el
  // stream directo. Mientras tanto el player muestra "Reintentando sin re-codificacion...".
  onTranscodeFailed?: () => void;
  // Como se ajusta el video al tamano de la celda:
  //   "contain" (default) = preserva aspect ratio, bandas negras si no calza.
  //   "fill"             = estira al tamano completo, puede deformar.
  //   "cover"            = llena y recorta lo que sobre.
  fitMode?: "contain" | "fill" | "cover";
  // Texto opcional mostrado dentro de un overlay grande con spinner durante el
  // estado "connecting". Pensado para Playback donde el handshake RTSP del NVR
  // puede tomar varios segundos. Live no lo usa (la conexion es casi instantanea).
  connectingHint?: string;
  // Playback-only props: control client-side del elemento <video> sin tocar
  // el stream backend. Estos NO reconectan go2rtc, solo manipulan el reproductor.
  //   paused: pausa/reanuda el frame visible.
  //   playbackRate: velocidad de reproduccion (0.5x..8x tipico).
  //   onTimeUpdate(elapsedSeconds): notifica al padre el avance del video real.
  //     Throttled a 250ms. Util para sincronizar el cursor del timeline.
  //   onStreamEnded: dispara cuando el video termina (event "ended") o queda
  //     stalled por >5s sin progreso. El padre lo usa para auto-advance al
  //     siguiente archivo del dia.
  paused?: boolean;
  playbackRate?: number;
  onTimeUpdate?: (elapsedSeconds: number) => void;
  onStreamEnded?: () => void;
}

export function WebRTCPlayer({ streamName, label, showAudioToggle = true, className, onStateChange, onDecodeError, onTranscodeFailed, fitMode = "contain", connectingHint, paused, playbackRate, onTimeUpdate, onStreamEnded }: Props) {
  // `audio` controla si pedimos al peer connection un transceiver de audio.
  // El default false ahorra ancho de banda; el toggle activa la negociacion
  // (lo que reconecta el stream — esperado).
  const [audio, setAudio] = useState(false);
  // `retryNonce` permite re-disparar la negociacion WebRTC sin recargar la pagina.
  // Cada incremento re-ejecuta el useEffect de useWebRTC.
  const [retryNonce, setRetryNonce] = useState(0);
  // Marcamos que ya disparamos onDecodeError para este streamName — evita loops
  // si el segundo intento (transcoded) también falla por alguna razón distinta.
  const [transcodeRequested, setTranscodeRequested] = useState(false);
  // Marcamos que ya disparamos onTranscodeFailed (fallback inverso transcode→directo).
  // Una vez por streamName, evita loops si el path directo tambien falla.
  const [transcodeFailureRequested, setTranscodeFailureRequested] = useState(false);
  // Zoom + pan con mouse: scroll-up = zoom in, scroll-down = zoom out, click+drag = pan.
  // `scale` clamped a [1,5]. Reset natural al volver a scale=1 (no hace falta botón).
  const [zoom, setZoom] = useState({ scale: 1, x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panStartRef = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  // Reset del nonce, flag de transcode y zoom cuando cambia el stream.
  useEffect(() => {
    setRetryNonce(0);
    setTranscodeRequested(false);
    setTranscodeFailureRequested(false);
    setZoom({ scale: 1, x: 0, y: 0 });
  }, [streamName]);
  const { videoRef, state, error } = useWebRTC(streamName, { audio }, retryNonce);

  // Guardamos el callback en un ref para llamarlo SOLO cuando `state` cambia
  // (no en cada render porque el padre pasa una arrow inline cuya referencia
  // cambia cada vez — incluirla en deps causa loop infinito si el callback
  // dispara setState en el padre).
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => { onStateChangeRef.current = onStateChange; });
  useEffect(() => {
    onStateChangeRef.current?.(state);
  }, [state]);

  // Detección del error de decode HEVC. Chrome emite el mensaje literal
  // "HTMLMediaElement.error attribute is not null" cuando appendBuffer falla
  // tras que el <video> ya entró en error por bitstream no-decodable. En vez
  // de mostrar el botón Reintentar (que volvería a fallar igual), pedimos al
  // padre transcoding a H.264 vía ffmpeg.
  const onDecodeErrorRef = useRef(onDecodeError);
  useEffect(() => { onDecodeErrorRef.current = onDecodeError; });
  const onTranscodeFailedRef = useRef(onTranscodeFailed);
  useEffect(() => { onTranscodeFailedRef.current = onTranscodeFailed; });
  useEffect(() => {
    if (state !== "error" || !error) return;
    // Path A: el wrapper transcode (`_h264`) cayo. Disparar fallback inverso:
    // limpiar localStorage del flag transcode y reintentar el path directo.
    // Esto va PRIMERO porque __TRANSCODE_FAILED__ es mas especifico y excluye
    // los otros casos (el stream `_h264` no podia haber generado HTMLMediaElement.error
    // por codec — el browser ya acepto el H.264 puro).
    if (!transcodeFailureRequested && onTranscodeFailedRef.current
        && error.includes("__TRANSCODE_FAILED__")) {
      setTranscodeFailureRequested(true);
      onTranscodeFailedRef.current();
      return;
    }
    if (!onDecodeErrorRef.current) return;
    if (transcodeRequested) return;
    // Path B: el path directo no decodifica HEVC, activar transcode.
    //   - HTMLMediaElement.error: el browser acepto el MIME pero el bitstream
    //     fallo en appendBuffer (bug del 0x60000000 en SPS HEVC).
    //   - __CODEC_UNSUPPORTED__: el browser no decodifica HEVC (isTypeSupported
    //     false) o go2rtc reporto codecs-not-matched al pedirle H264 nativo.
    const needsTranscode =
      error.includes("HTMLMediaElement.error") ||
      error.includes("__CODEC_UNSUPPORTED__");
    if (needsTranscode) {
      setTranscodeRequested(true);
      onDecodeErrorRef.current();
    }
  }, [state, error, transcodeRequested, transcodeFailureRequested]);

  // Playback-only: aplicar pause/resume client-side al <video> sin tocar el
  // WS/stream. Solo aplica una vez que el player esta playing (antes hace
  // efecto cuando el frame todavia no llego).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || paused === undefined) return;
    if (paused) {
      try { video.pause(); } catch {}
    } else {
      try { video.play().catch(() => {}); } catch {}
    }
  }, [paused, state, videoRef]);

  // Playback-only: aplicar velocidad de reproduccion. Re-aplica cuando cambia
  // streamName porque el HTMLVideoElement se reinicia con cada nuevo MSE.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const rate = playbackRate ?? 1;
    if (video.playbackRate !== rate) {
      try { video.playbackRate = rate; } catch {}
    }
  }, [playbackRate, streamName, state, videoRef]);

  // Playback-only: timeupdate throttled a 250ms. Notifica al padre el avance
  // real del video para que sincronice el cursor del timeline. Sin throttle
  // se dispararia ~60Hz y haria render del padre constantemente.
  const onTimeUpdateRef = useRef(onTimeUpdate);
  useEffect(() => { onTimeUpdateRef.current = onTimeUpdate; });
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onTimeUpdate) return;
    let lastEmitMs = 0;
    const handler = () => {
      const now = performance.now();
      if (now - lastEmitMs < 250) return;
      lastEmitMs = now;
      onTimeUpdateRef.current?.(video.currentTime);
    };
    video.addEventListener("timeupdate", handler);
    return () => video.removeEventListener("timeupdate", handler);
  }, [streamName, videoRef, onTimeUpdate]);

  // Playback-only: detectar fin del stream para auto-advance al siguiente
  // archivo del dia. Dos casos:
  //   - event "ended": el <video> termino limpio.
  //   - stall prolongado: la posicion no avanza por >10s mientras debiamos
  //     estar reproduciendo. Caso tipico del playback RTSP cuando el NVR
  //     corta tras entregar el final del archivo .dav sin enviar EOF.
  // CRITICO: el detector solo se monta cuando state === "playing". Durante
  // "connecting" (handshake RTSP, varios segundos en VPN), currentTime queda
  // en 0 fijo y el stall counter lo confundiria con "stream estancado",
  // disparando un banner "fin de grabaciones" falso antes de que el video
  // siquiera arranque. Esperar a "playing" garantiza que el video ya tenia
  // al menos un frame y currentTime > 0.
  const onStreamEndedRef = useRef(onStreamEnded);
  useEffect(() => { onStreamEndedRef.current = onStreamEnded; });
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !onStreamEnded) return;
    if (state !== "playing") return;
    let endedFired = false;
    const fireOnce = () => {
      if (endedFired) return;
      endedFired = true;
      onStreamEndedRef.current?.();
    };
    const onEnded = () => fireOnce();
    video.addEventListener("ended", onEnded);
    let lastTime = video.currentTime;
    let stalledSince = 0;
    const stallInterval = window.setInterval(() => {
      if (endedFired) return;
      if (video.paused || video.seeking) {
        stalledSince = 0;
        lastTime = video.currentTime;
        return;
      }
      if (video.currentTime === lastTime) {
        if (stalledSince === 0) stalledSince = performance.now();
        else if (performance.now() - stalledSince > 10000) fireOnce();
      } else {
        stalledSince = 0;
        lastTime = video.currentTime;
      }
    }, 1000);
    return () => {
      video.removeEventListener("ended", onEnded);
      clearInterval(stallInterval);
    };
  }, [streamName, videoRef, onStreamEnded, state]);

  // Wheel listener manual con passive: false para poder hacer preventDefault.
  // React por defecto registra los onWheel como passive y bloquearía preventDefault.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;
      if (W === 0 || H === 0) return;
      const offsetX = e.clientX - rect.left - W / 2;
      const offsetY = e.clientY - rect.top - H / 2;
      setZoom((prev) => {
        const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
        const next = Math.max(1, Math.min(5, prev.scale * factor));
        if (next === prev.scale) return prev;
        if (next === 1) return { scale: 1, x: 0, y: 0 };
        const ratio = next / prev.scale;
        const nx = offsetX - (offsetX - prev.x) * ratio;
        const ny = offsetY - (offsetY - prev.y) * ratio;
        const maxX = (W * (next - 1)) / 2;
        const maxY = (H * (next - 1)) / 2;
        return {
          scale: next,
          x: Math.max(-maxX, Math.min(maxX, nx)),
          y: Math.max(-maxY, Math.min(maxY, ny)),
        };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (zoom.scale <= 1) return;
    if (e.button !== 0) return;
    const el = containerRef.current;
    if (!el) return;
    el.setPointerCapture(e.pointerId);
    panStartRef.current = { x: e.clientX, y: e.clientY, tx: zoom.x, ty: zoom.y };
    setIsPanning(true);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = panStartRef.current;
    if (!start) return;
    const el = containerRef.current;
    if (!el) return;
    const W = el.clientWidth;
    const H = el.clientHeight;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    setZoom((prev) => {
      if (prev.scale <= 1) return prev;
      const maxX = (W * (prev.scale - 1)) / 2;
      const maxY = (H * (prev.scale - 1)) / 2;
      return {
        scale: prev.scale,
        x: Math.max(-maxX, Math.min(maxX, start.tx + dx)),
        y: Math.max(-maxY, Math.min(maxY, start.ty + dy)),
      };
    });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!panStartRef.current) return;
    panStartRef.current = null;
    setIsPanning(false);
    const el = containerRef.current;
    if (el && el.hasPointerCapture(e.pointerId)) {
      try { el.releasePointerCapture(e.pointerId); } catch {}
    }
  };

  const toggleAudio = () => {
    setAudio((a) => {
      const next = !a;
      // Sincronizar muted con el estado de audio: si activamos audio, desmutear.
      if (videoRef.current) videoRef.current.muted = !next;
      return next;
    });
  };

  const fitClass = fitMode === "fill" ? "object-fill"
    : fitMode === "cover" ? "object-cover"
    : "object-contain";

  const videoStyle = {
    transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
    transformOrigin: "center center" as const,
    transition: isPanning ? "none" : "transform 80ms linear",
  };
  const containerStyle = zoom.scale > 1
    ? { cursor: isPanning ? "grabbing" : "grab" }
    : undefined;

  return (
    <div
      ref={containerRef}
      className={clsx("relative w-full h-full bg-black overflow-hidden", className)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={containerStyle}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={!audio}
        className={clsx("w-full h-full bg-black", fitClass)}
        style={videoStyle}
      />
      {/* Estado / overlay */}
      {state !== "playing" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted text-xs bg-black/40">
          {state === "connecting" && connectingHint && (
            <div className="flex flex-col items-center gap-2 text-blue-200 px-4 text-center">
              <Loader2 className="animate-spin" size={32} />
              <span className="text-sm font-medium">{connectingHint}</span>
              {streamName && streamName.endsWith("_h264") && (
                <span className="text-muted text-[10px]">Re-codificando HEVC → H.264 (5-15s)</span>
              )}
            </div>
          )}
          {state === "connecting" && !connectingHint && (
            <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Conectando...</span>
          )}
          {state === "error" && transcodeFailureRequested && (
            <div className="flex flex-col items-center gap-1 text-amber-400 px-4 text-center">
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Reintentando sin re-codificacion...
              </span>
              <span className="text-muted text-[10px]">El path transcode no respondio; probando RTSP directo</span>
            </div>
          )}
          {state === "error" && transcodeRequested && !transcodeFailureRequested && (
            <div className="flex flex-col items-center gap-1 text-amber-400 px-4 text-center">
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} /> Re-codificando a H.264...
              </span>
              <span className="text-muted text-[10px]">Puede tardar 5-15 segundos</span>
            </div>
          )}
          {state === "error" && !transcodeRequested && !transcodeFailureRequested && (
            <>
              <span className="text-red-400 px-4 text-center">Error: {(error || "stream caido").replace(/^__(CODEC_UNSUPPORTED|TRANSCODE_FAILED)__:\s*/, "")}</span>
              <button
                onClick={() => setRetryNonce((n) => n + 1)}
                className="flex items-center gap-1 bg-accent/80 hover:bg-accent text-white px-2 py-1 rounded text-xs"
              >
                <RotateCw size={12} /> Reintentar
              </button>
            </>
          )}
          {state === "idle" && <span>Sin senal</span>}
          {state === "stopped" && <span>Detenido</span>}
        </div>
      )}

      {/* Overlay controles */}
      {streamName && (
        <div className="absolute top-1 left-1 right-1 flex items-center justify-between text-xs pointer-events-none">
          <span className="bg-black/60 text-white px-1.5 py-0.5 rounded text-[11px]">
            {label || streamName}
          </span>
          {showAudioToggle && state === "playing" && (
            <button
              onClick={toggleAudio}
              aria-label={audio ? "Silenciar audio" : "Activar audio"}
              className="bg-black/60 text-white p-1 rounded pointer-events-auto hover:bg-black/80"
              title={audio ? "Silenciar audio" : "Activar audio (puede reconectar)"}
            >
              {audio ? <Volume2 size={12} /> : <VolumeX size={12} />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
